import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { X, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { useOrgPath } from '@/lib/pathFor'
import PortCombobox from '@/components/ports/PortCombobox'

interface Client {
  id: string
  name: string
}

interface CreateShipmentModalProps {
  open: boolean
  onClose: () => void
  emailThreadId?: string | null
  clients: Client[]
}

const selectClasses = "mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-[13px] shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer"

export default function CreateShipmentModal({ open, onClose, emailThreadId, clients }: CreateShipmentModalProps) {
  const pathFor = useOrgPath()
  const { props } = usePage<{ errors?: Record<string, string[] | string> }>()
  const [processing, setProcessing] = useState(false)
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({})
  const [selectedRouteValid, setSelectedRouteValid] = useState({ origin_port: true, destination_port: true })
  const [data, setData] = useState({
    client_id: '',
    origin_port: '',
    destination_port: '',
    commodity: '',
  })

  if (!open) return null

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const nextErrors: Record<string, string> = {}
    if (!selectedRouteValid.origin_port) nextErrors.origin_port = 'Select a port from the list.'
    if (!selectedRouteValid.destination_port) nextErrors.destination_port = 'Select a port from the list.'
    setLocalErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    setProcessing(true)

    router.post(pathFor('/shipments'), {
      shipment: data,
      email_thread_id: emailThreadId || undefined,
    }, {
      onSuccess: () => {
        toast.success('Shipment created')
        onClose()
        setData({ client_id: '', origin_port: '', destination_port: '', commodity: '' })
      },
      onError: () => {
        toast.error('Failed to create shipment')
      },
      onFinish: () => setProcessing(false),
    })
  }

  function clearLocalError(field: string) {
    setLocalErrors((current) => {
      if (!current[field]) return current
      const next = { ...current }
      delete next[field]
      return next
    })
  }

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose()
  }

  const buttonText = emailThreadId ? 'Create & Link' : 'Create'
  const errors = props.errors || {}
  const fieldError = (field: string) => {
    const error = localErrors[field] || errors[field]
    return Array.isArray(error) ? error.join(', ') : error
  }
  const hasRouteError = Boolean(fieldError('origin_port') || fieldError('destination_port'))

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-xl shadow-lg" style={{ width: 440 }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <h2 className="text-base font-semibold text-foreground">Create New Shipment</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted cursor-pointer"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 pb-5 space-y-4">
          {/* Client Dropdown */}
          <div>
            <Label htmlFor="client_id" className="text-xs">Client</Label>
            <select
              id="client_id"
              value={data.client_id}
              onChange={(e) => setData({ ...data, client_id: e.target.value })}
              className={selectClasses}
            >
              <option value="">Select a client...</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="origin_port" className="text-xs">Origin Port</Label>
              <PortCombobox
                inputId="origin_port"
                placeholder="e.g. CNSHA"
                value={data.origin_port}
                onChange={(value) => {
                  setData({ ...data, origin_port: value })
                  clearLocalError('origin_port')
                }}
                onValidityChange={(valid) => setSelectedRouteValid((state) => ({ ...state, origin_port: valid }))}
                disabled={processing}
                invalidMessage={fieldError('origin_port')}
              />
            </div>
            <div>
              <Label htmlFor="destination_port" className="text-xs">Destination Port</Label>
              <PortCombobox
                inputId="destination_port"
                placeholder="e.g. USLAX"
                value={data.destination_port}
                onChange={(value) => {
                  setData({ ...data, destination_port: value })
                  clearLocalError('destination_port')
                }}
                onValidityChange={(valid) => setSelectedRouteValid((state) => ({ ...state, destination_port: valid }))}
                disabled={processing}
                invalidMessage={fieldError('destination_port')}
              />
            </div>
          </div>

          {/* Commodity */}
          <div>
            <Label htmlFor="commodity" className="text-xs">Commodity (optional)</Label>
            <Input
              id="commodity"
              placeholder="e.g. Electronics, Textiles..."
              value={data.commodity}
              onChange={(e) => setData({ ...data, commodity: e.target.value })}
              className="mt-1 h-9 text-[13px]"
            />
          </div>

          {/* Auto-link hint (when emailThreadId present) */}
          {emailThreadId && (
            <div className="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 px-3 py-2.5">
              <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-800">
                This thread will be linked to the new shipment.
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center gap-2 pt-2">
            <div className="min-h-4 flex-1">
              {hasRouteError && (
                <p role="alert" className="truncate text-xs font-medium text-red-600">
                  Select highlighted ports from the dropdown.
                </p>
              )}
            </div>
            <Button type="button" variant="outline" size="sm" className="cursor-pointer" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" className="cursor-pointer" disabled={processing}>
              {buttonText}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
